<script lang="ts">
	import { format } from 'svelte-i18n';
	// Helpers
	import { findClosestAngle } from '../weather_station_helpers';
	// Components
	import WeatherStationCard from './WeatherStationCard.svelte';
	// Types
	import type { PressureTrend } from '../weather_station_cloud_helpers';

	export let statuses;
	export let elevation: string;
	export let pressureTrendLabel: PressureTrend;
	export let setLocationAlert: boolean = false;
	export let disableSetLocationAlert: boolean = false;

	const BAR_MIN = 300;
	const BAR_MAX = 1200;
	const BAR_STEP = 50;

	const getAngleTrend = (trend: string): number => {
		if (trend === 'STEADY') return 270;
		return trend === 'DOWN' ? 0 : 180;
	};

	let pressureDirection = 270;
	$: pressure = statuses.find((i) => i.name === 'pressure:0')?.rawValue;
	$: pressureValue = Math.min(
		BAR_MAX,
		Math.max(BAR_MIN, Math.round((pressure ?? 0) / BAR_STEP) * BAR_STEP),
	);
	$: {
		let angleTrend = getAngleTrend(pressureTrendLabel);
		if (pressureDirection != angleTrend) {
			pressureDirection = findClosestAngle(pressureDirection, angleTrend);
		}
	}
</script>

<WeatherStationCard
	icon="far fa-gauge-simple-high"
	label={$format('icon_name_pressure', { default: 'Pressure' })}
	description={$format('label_elevation', { default: 'Elevation' })}
	descriptionValue={elevation}
	{setLocationAlert}
	{disableSetLocationAlert}
	setLocationAlertTitle={$format('alert_weather_station_set_location_pressure_info', {
		default: 'Set your location to view pressure conditions.',
	})}
	on:click
>
	<div slot="main" class="d-flex">
		<svg width="120" height="120" viewBox="0 0 120 120" fill="none">
			<g id="pressure-gauge" data-active={pressureValue}>
				<path
					data-val="300"
					d="M28.0533 96.8633L25.0979 99.7973"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="350"
					d="M21.3622 88.4355L17.7986 90.6036"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="400"
					d="M16.8378 78.6924L12.8628 79.9746"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="450"
					d="M14.7292 68.1689L10.5625 68.4945"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="500"
					d="M15.1535 57.4489L11.0254 56.7998"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="550"
					d="M18.0872 47.122L14.2258 45.5342"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="600"
					d="M23.3679 37.7602L19.9866 35.3213"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="650"
					d="M30.7042 29.8827L27.9897 26.7275"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="700"
					d="M39.6902 23.9197L37.7927 20.2227"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="750"
					d="M49.8297 20.2075L48.854 16.1729"
					stroke="#818588"
					stroke-width="2"
				/>
				<path data-val="800" d="M60.5625 18.9462V14.7969" stroke="#818588" stroke-width="2" />
				<path
					data-val="850"
					d="M71.2952 20.2075L72.2708 16.1729"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="900"
					d="M81.4346 23.9197L83.332 20.2227"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="950"
					d="M90.4209 29.8827L93.1353 26.7275"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1000"
					d="M97.7571 37.7602L101.138 35.3213"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1050"
					d="M103.038 47.122L106.899 45.5342"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1100"
					d="M105.971 57.4489L110.1 56.7998"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1150"
					d="M106.396 68.1689L110.562 68.4945"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1200"
					d="M104.287 78.6924L108.262 79.9746"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1250"
					d="M99.7627 88.4355L103.326 90.6036"
					stroke="#818588"
					stroke-width="2"
				/>
				<path
					data-val="1300"
					d="M93.0718 96.8633L96.0272 99.7973"
					stroke="#818588"
					stroke-width="2"
				/>
			</g>
			<path
				d="M6.87416 109.699C6.38647 109.699 5.95087 109.615 5.56734 109.447C5.18619 109.279 4.88434 109.046 4.6618 108.747C4.44163 108.447 4.32326 108.099 4.30669 107.703H5.42175C5.43595 107.919 5.50816 108.106 5.63837 108.264C5.77094 108.421 5.94376 108.541 6.15683 108.627C6.3699 108.712 6.60664 108.754 6.86706 108.754C7.15352 108.754 7.40683 108.705 7.627 108.605C7.84954 108.506 8.02355 108.367 8.14902 108.19C8.27449 108.01 8.33723 107.803 8.33723 107.568C8.33723 107.325 8.27449 107.11 8.14902 106.926C8.02591 106.739 7.8448 106.592 7.6057 106.485C7.36895 106.379 7.08249 106.325 6.74632 106.325H6.13197V105.431H6.74632C7.01621 105.431 7.25295 105.382 7.45655 105.285C7.66251 105.188 7.8235 105.053 7.9395 104.88C8.05551 104.705 8.11351 104.5 8.11351 104.266C8.11351 104.041 8.06261 103.846 7.96081 103.68C7.86138 103.512 7.71933 103.38 7.53467 103.286C7.35238 103.191 7.13695 103.144 6.88837 103.144C6.65162 103.144 6.43027 103.187 6.2243 103.275C6.0207 103.36 5.85498 103.483 5.72714 103.644C5.5993 103.803 5.53065 103.994 5.52118 104.216H4.45939C4.47123 103.823 4.58723 103.477 4.8074 103.179C5.02994 102.881 5.3235 102.648 5.68808 102.48C6.05266 102.311 6.45749 102.227 6.90257 102.227C7.36895 102.227 7.77141 102.319 8.10996 102.501C8.45087 102.681 8.71365 102.921 8.89831 103.222C9.08534 103.522 9.17766 103.852 9.1753 104.209C9.17766 104.616 9.06403 104.962 8.83439 105.246C8.60712 105.53 8.30409 105.721 7.9253 105.818V105.874C8.40825 105.948 8.78231 106.14 9.04746 106.45C9.31498 106.76 9.44755 107.145 9.44518 107.604C9.44755 108.004 9.33628 108.363 9.11138 108.68C8.88884 108.997 8.58462 109.247 8.19873 109.429C7.81284 109.609 7.37132 109.699 6.87416 109.699ZM12.7195 109.72C12.1584 109.718 11.679 109.57 11.2813 109.276C10.8835 108.983 10.5793 108.556 10.3686 107.994C10.1579 107.433 10.0526 106.758 10.0526 105.967C10.0526 105.178 10.1579 104.505 10.3686 103.946C10.5817 103.387 10.8871 102.961 11.2848 102.668C11.6849 102.374 12.1631 102.227 12.7195 102.227C13.2758 102.227 13.7529 102.375 14.1506 102.671C14.5483 102.965 14.8525 103.391 15.0632 103.95C15.2763 104.506 15.3828 105.178 15.3828 105.967C15.3828 106.76 15.2775 107.437 15.0668 107.998C14.8561 108.557 14.5519 108.984 14.1541 109.28C13.7564 109.574 13.2782 109.72 12.7195 109.72ZM12.7195 108.772C13.2119 108.772 13.5966 108.532 13.8736 108.051C14.1529 107.571 14.2926 106.876 14.2926 105.967C14.2926 105.363 14.2287 104.853 14.1009 104.436C13.9754 104.017 13.7943 103.7 13.5575 103.485C13.3232 103.267 13.0438 103.158 12.7195 103.158C12.2294 103.158 11.8447 103.399 11.5654 103.882C11.286 104.365 11.1451 105.06 11.1428 105.967C11.1428 106.573 11.2055 107.085 11.331 107.504C11.4588 107.921 11.6399 108.237 11.8743 108.453C12.1087 108.666 12.3904 108.772 12.7195 108.772ZM18.5746 109.72C18.0135 109.718 17.5341 109.57 17.1363 109.276C16.7386 108.983 16.4344 108.556 16.2237 107.994C16.013 107.433 15.9076 106.758 15.9076 105.967C15.9076 105.178 16.013 104.505 16.2237 103.946C16.4368 103.387 16.7422 102.961 17.1399 102.668C17.54 102.374 18.0182 102.227 18.5746 102.227C19.1309 102.227 19.6079 102.375 20.0057 102.671C20.4034 102.965 20.7076 103.391 20.9183 103.95C21.1314 104.506 21.2379 105.178 21.2379 105.967C21.2379 106.76 21.1326 107.437 20.9219 107.998C20.7112 108.557 20.4069 108.984 20.0092 109.28C19.6115 109.574 19.1333 109.72 18.5746 109.72ZM18.5746 108.772C19.067 108.772 19.4517 108.532 19.7287 108.051C20.008 107.571 20.1477 106.876 20.1477 105.967C20.1477 105.363 20.0838 104.853 19.9559 104.436C19.8305 104.017 19.6494 103.7 19.4126 103.485C19.1782 103.267 18.8989 103.158 18.5746 103.158C18.0845 103.158 17.6998 103.399 17.4204 103.882C17.1411 104.365 17.0002 105.06 16.9978 105.967C16.9978 106.573 17.0606 107.085 17.1861 107.504C17.3139 107.921 17.495 108.237 17.7294 108.453C17.9638 108.666 18.2455 108.772 18.5746 108.772Z"
				fill="#818588"
			/>
			<path
				d="M97.5942 102.327V109.6H96.4934V103.428H96.4507L94.7107 104.564V103.513L96.5253 102.327H97.5942ZM98.9615 109.6V108.804L101.422 106.254C101.685 105.977 101.902 105.735 102.072 105.526C102.245 105.316 102.374 105.116 102.459 104.926C102.545 104.737 102.587 104.536 102.587 104.323C102.587 104.081 102.53 103.873 102.417 103.698C102.303 103.52 102.148 103.384 101.952 103.289C101.755 103.192 101.534 103.144 101.288 103.144C101.027 103.144 100.8 103.197 100.606 103.303C100.412 103.41 100.262 103.56 100.158 103.754C100.054 103.949 100.002 104.176 100.002 104.436H98.9544C98.9544 103.994 99.0562 103.606 99.2598 103.275C99.4634 102.944 99.7428 102.687 100.098 102.504C100.453 102.32 100.857 102.227 101.309 102.227C101.766 102.227 102.168 102.319 102.516 102.501C102.867 102.681 103.14 102.927 103.337 103.24C103.533 103.55 103.631 103.9 103.631 104.291C103.631 104.561 103.58 104.825 103.479 105.083C103.379 105.341 103.205 105.628 102.957 105.945C102.708 106.26 102.362 106.643 101.92 107.093L100.474 108.605V108.659H103.748V109.6H98.9615ZM107.026 109.72C106.465 109.718 105.986 109.57 105.588 109.276C105.19 108.983 104.886 108.556 104.675 107.994C104.465 107.433 104.359 106.758 104.359 105.967C104.359 105.178 104.465 104.505 104.675 103.946C104.889 103.387 105.194 102.961 105.592 102.668C105.992 102.374 106.47 102.227 107.026 102.227C107.583 102.227 108.06 102.375 108.457 102.671C108.855 102.965 109.159 103.391 109.37 103.95C109.583 104.506 109.69 105.178 109.69 105.967C109.69 106.76 109.584 107.437 109.374 107.998C109.163 108.557 108.859 108.984 108.461 109.28C108.063 109.574 107.585 109.72 107.026 109.72ZM107.026 108.772C107.519 108.772 107.903 108.532 108.18 108.051C108.46 107.571 108.599 106.876 108.599 105.967C108.599 105.363 108.536 104.853 108.408 104.436C108.282 104.017 108.101 103.7 107.864 103.485C107.63 103.267 107.351 103.158 107.026 103.158C106.536 103.158 106.152 103.399 105.872 103.882C105.593 104.365 105.452 105.06 105.45 105.967C105.45 106.573 105.512 107.085 105.638 107.504C105.766 107.921 105.947 108.237 106.181 108.453C106.416 108.666 106.697 108.772 107.026 108.772ZM112.881 109.72C112.32 109.718 111.841 109.57 111.443 109.276C111.045 108.983 110.741 108.556 110.531 107.994C110.32 107.433 110.214 106.758 110.214 105.967C110.214 105.178 110.32 104.505 110.531 103.946C110.744 103.387 111.049 102.961 111.447 102.668C111.847 102.374 112.325 102.227 112.881 102.227C113.438 102.227 113.915 102.375 114.312 102.671C114.71 102.965 115.014 103.391 115.225 103.95C115.438 104.506 115.545 105.178 115.545 105.967C115.545 106.76 115.439 107.437 115.229 107.998C115.018 108.557 114.714 108.984 114.316 109.28C113.918 109.574 113.44 109.72 112.881 109.72ZM112.881 108.772C113.374 108.772 113.759 108.532 114.036 108.051C114.315 107.571 114.455 106.876 114.455 105.967C114.455 105.363 114.391 104.853 114.263 104.436C114.137 104.017 113.956 103.7 113.719 103.485C113.485 103.267 113.206 103.158 112.881 103.158C112.391 103.158 112.007 103.399 111.727 103.882C111.448 104.365 111.307 105.06 111.305 105.967C111.305 106.573 111.367 107.085 111.493 107.504C111.621 107.921 111.802 108.237 112.036 108.453C112.271 108.666 112.552 108.772 112.881 108.772Z"
				fill="#818588"
			/>
			<text
				x="50%"
				y="50%"
				fill="#EDEDED"
				font-size="14"
				font-weight="700"
				width="100%"
				fill-opacity="0.96"
				dominant-baseline="middle"
				text-anchor="middle">{pressure ?? 'N/A'}</text
			>
			<text
				x="50%"
				y="65%"
				fill="#EDEDED"
				font-size="16"
				width="100%"
				fill-opacity="0.96"
				dominant-baseline="middle"
				text-anchor="middle">hPa</text
			>
			<g id="pressure-arrow" transform={`rotate(${pressureDirection})`}>
				<path
					d="M59.5946 42.661C60.1299 43.1135 60.9992 43.1135 61.5345 42.661L68.3859
			36.8692C68.9212 36.4167 68.9212 35.6819 68.3859 35.2294C67.8507 34.7769 66.9814
			34.7769 66.4461 35.2294L61.9327 39.0484V27.9425C61.9327 27.3018 61.3203 26.7842
			60.5624 26.7842C59.8045 26.7842 59.1921 27.3018 59.1921 27.9425V39.0448L54.6787
			35.233C54.1434 34.7805 53.2741 34.7805 52.7389 35.233C52.2036 35.6855 52.2036
			36.4203 52.7389 36.8728L59.5903 42.6647L59.5946 42.661Z"
					fill="#EDEDED"
					fill-opacity="0.96"
				/>
			</g>
		</svg>
	</div>
</WeatherStationCard>

<style>
	svg {
		transform: scale(1.05);
		margin-top: -10px;
		max-width: 100%;
	}
	svg g#pressure-arrow {
		transform-origin: 50.45% 29%;
		transition: transform 1s ease-in-out;
	}

	#pressure-gauge path {
		stroke: #818588;
		stroke-width: 2;
		transition: stroke 0.3s;
	}
	#pressure-gauge[data-active='300'] [data-val='300'],
	#pressure-gauge[data-active='350'] [data-val='350'],
	#pressure-gauge[data-active='400'] [data-val='400'],
	#pressure-gauge[data-active='450'] [data-val='450'],
	#pressure-gauge[data-active='500'] [data-val='500'],
	#pressure-gauge[data-active='550'] [data-val='550'],
	#pressure-gauge[data-active='600'] [data-val='600'],
	#pressure-gauge[data-active='650'] [data-val='650'],
	#pressure-gauge[data-active='700'] [data-val='700'],
	#pressure-gauge[data-active='750'] [data-val='750'],
	#pressure-gauge[data-active='800'] [data-val='800'],
	#pressure-gauge[data-active='850'] [data-val='850'],
	#pressure-gauge[data-active='900'] [data-val='900'],
	#pressure-gauge[data-active='950'] [data-val='950'],
	#pressure-gauge[data-active='1000'] [data-val='1000'],
	#pressure-gauge[data-active='1050'] [data-val='1050'],
	#pressure-gauge[data-active='1100'] [data-val='1100'],
	#pressure-gauge[data-active='1150'] [data-val='1150'],
	#pressure-gauge[data-active='1200'] [data-val='1200'] {
		stroke: #ededed;
		stroke-width: 2;
		stroke-linecap: round;
		transform-box: fill-box;
		transform-origin: center;
		transform: scale(1.5);
	}
</style>
